/**
 * StreakIndicator — показывает игроку текущий ритм-стрик.
 *
 * JB 2026-05-25: «стрик то юзеру как то видно должно быть». Сейчас
 * `state.stats.tapsInRhythmCurrent` живёт в стейте и при streak ≥5
 * меняется звук тапа на `tap_ding_streak`, но визуально игрок не видит
 * сколько у него подряд тапов в ритм.
 *
 * Видимость:
 *   - streak < 3 → ничего не рендерим (тихий старт)
 *   - 3-4         → мягкий cream badge (набирается)
 *   - 5-9         → gold pulse (звук streak уже играет — visual должен
 *                  совпадать с этим порогом)
 *   - 10-24       → более яркий gold + ускоренная пульсация
 *   - 25+         → bright gold + sharper pulse (peak rhythm)
 *
 * Появление — fade-in 200ms, исчезновение когда стрик прерывается —
 * fade-out 500ms. Бесшовный флоу (#12) — никаких резких переходов.
 *
 * Wire: SparkScreen `.stats` section, рядом с AwarenessCounter +
 * MultiplierBadge.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useAnchor } from '../onboarding/useAnchor.js';
import { useGameStore } from '../stores/game.js';
import styles from './StreakIndicator.module.css';

/** Минимальный streak для появления — ниже скрыто чтобы не отвлекать. */
const STREAK_THRESHOLD_SHOW = 3;
/** Порог где звук тапа меняется на streak (см. Spark.tsx). */
const STREAK_THRESHOLD_AUDIO = 5;

export function StreakIndicator(): JSX.Element {
  const streak = useGameStore((s) => s.state.stats.tapsInRhythmCurrent);
  const streakBest = useGameStore((s) => s.state.stats.tapsInRhythmBest);
  // Регистрируем slot как onboarding anchor — даже когда streak < 3 и
  // бэйдж не виден, slot занимает место рядом со счётчиком, чтобы tip
  // про ритм мог в него прицелиться.
  const slotRef = useAnchor<HTMLDivElement>('streak');

  const visible = streak >= STREAK_THRESHOLD_SHOW;

  // Tier — чтобы менять цвет/пульсацию.
  const tier = (() => {
    if (streak >= 25) return 'peak';
    if (streak >= 10) return 'high';
    if (streak >= STREAK_THRESHOLD_AUDIO) return 'audio';
    return 'soft';
  })();

  const tierClass = (() => {
    switch (tier) {
      case 'peak':
        return styles.peak ?? '';
      case 'high':
        return styles.high ?? '';
      case 'audio':
        return styles.audio ?? '';
      case 'soft':
        return styles.soft ?? '';
    }
  })();

  return (
    <div ref={slotRef} className={styles.slot} aria-live="polite">
      <AnimatePresence>
        {visible && (
          <motion.div
            key="badge"
            className={`${styles.badge ?? ''} ${tierClass}`}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            // JB 2026-05-25: fade-out длиннее (1.2s вместо 0.25s) чтобы
            // потеря стрика чувствовалась как медленное растворение, не
            // резкое исчезновение. Триггер сброса — store.fadeStreak()
            // через 2.5s тишины после последнего тапа.
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{
              opacity: { duration: 1.2, ease: 'easeOut' },
              scale: { duration: 0.4, ease: [0.25, 0.8, 0.3, 1] },
            }}
            aria-label={`Ритм-стрик: ${streak.toString()}`}
          >
            <span className={styles.symbol} aria-hidden>
              ◈
            </span>
            <span className={styles.value}>×{streak.toString()}</span>
            <span className={styles.label}>в ритме</span>
            {/* Personal best — Doc 28 §E: «игрок не знает свой PR».
                Показываем мелким шрифтом если есть и только когда current
                СТРОГО меньше best (есть к чему стремиться) либо равен ему
                (только что побит). 2026-05-26: было «pr 5» — английская
                аббревиатура мусором торчала, заменяем на «★» = чистый
                символ, понятный без локали и без жаргона. */}
            {streakBest >= 5 && streak < streakBest && (
              <span className={styles.best} aria-label={`Личный рекорд ${streakBest.toString()}`}>
                ★ {streakBest.toString()}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
